import {useCallback, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {Formik} from 'formik'
import {LockIcon} from '../../../icons'
import Input from '../../core/input'
import {SimpleBlueButton} from '../../core/button'
import ButtonLoader from '../../core/button/ButtonLoader'
import Forms from '../../../utils/forms'
import {getPasswordErrors} from '../../../utils/validation'
import {updatePassword} from '../../../stores/customer-settings'
import {trimStringValues} from '../../../utils/text'

const names = {
    previous_password: 'previous_password',
    proposed_password: 'proposed_password',
    confirm_new_password: 'confirm_new_password'
}

const PasswordSection = ({text}) => {
    const dispatch = useDispatch()
    const isDarkMode = useSelector(state => state.sidebar.isDarkMode)
    const [active, setActive] = useState(null)
    const [expanded, setExpanded] = useState(false)
    const [formErrors, setFormErrors] = useState({
        previous_password: [],
        proposed_password: [],
        confirm_new_password: []
    })

    const initialFormValues = {
        previous_password: '',
        proposed_password: '',
        confirm_new_password: ''
    }

    const updateCustomerPassword = async(values, {setFieldError}) => {
        const data = {
            previous_password: values.previous_password,
            proposed_password: values.proposed_password
        }
        await dispatch(updatePassword(trimStringValues(data), setFieldError))
    }

    const handleInfo = (index, state) => {
        state ? setActive(index) : setActive(null)
    }

    const checkIsValid = (values, name) => {
        return values?.[name] && formErrors?.[name].every(item => !item.status)
    }

    const validateInputs = useCallback(async(e, name, oldValue) => {
        const passwordErrors = await getPasswordErrors()
        const value = e.target.value
        const errors = []
        const validations = passwordErrors[name]

        validations.forEach((item) => {
            errors.push({
                status: !item.validator(value, oldValue),
                message: item.message
            })
        })

        setFormErrors(prev => ({
            ...prev,
            [name]: errors
        }))
    }, [])

    const getButtonStatus = (values, isSubmitting) => {
        return !checkIsValid(values, names.previous_password) ||
            !checkIsValid(values, names.proposed_password) ||
            !checkIsValid(values, names.confirm_new_password) ||
            isSubmitting
    }

    const handleCancel = (resetForm) => {
        resetForm()
        setFormErrors({
            previous_password: [],
            proposed_password: [],
            confirm_new_password: []
        })
        setActive(null)
        setExpanded(false)
    }

    return (
        <div className='security-card'>
            <div className='security-card-content'>
                <div className='security-icon security-icon--neutral'>
                    <LockIcon fill='currentColor' width={18} height={18}/>
                </div>
                <div className='security-info'>
                    <h3 className='security-card-title'>{text.password_title}</h3>
                    <p className='security-card-description'>{text.password_description}</p>
                </div>
                {!expanded && (
                    <SimpleBlueButton
                        outline
                        className='security-edit-button'
                        onClick={() => setExpanded(true)}
                    >
                        {text.change_password}
                    </SimpleBlueButton>
                )}
            </div>
            {expanded && (
                <div className='security-card-body'>
                    <Formik
                        initialValues={initialFormValues}
                        onSubmit={updateCustomerPassword}
                        validateOnBlur={false}
                        validateOnChange={true}
                    >
                        {({
                            values, errors, touched, handleChange, handleSubmit, setFieldTouched, setTouched, resetForm, isSubmitting
                        }) => (
                            <form onSubmit={handleSubmit} className='security-form'>
                                <div className="input-container">
                                    <Input
                                        type="password"
                                        value={values.previous_password}
                                        placeholder={text.current_password}
                                        infoText={text.current_password_info}
                                        name={names.previous_password}
                                        onChange={e => {
                                            validateInputs(e, names.previous_password)
                                            handleChange(e)
                                        }}
                                        onFocus={() => {
                                            setFieldTouched(names.previous_password)
                                        }}
                                        onBlur={() => setTouched({})}
                                        error={errors.previous_password}
                                        activeInfo={active === names.previous_password}
                                        handleInfo={(state) => handleInfo(names.previous_password, state)}
                                        isTouched={touched.previous_password}
                                        isCheckedPass
                                        icon={checkIsValid(values, names.previous_password)}
                                        hasRealTimeValidation={true}
                                    />
                                    {Forms.getValidationToolTip(checkIsValid, values, names.previous_password, formErrors, touched, text.password_requirments, isDarkMode)}
                                </div>
                                <div className="input-container">
                                    <Input
                                        type="password"
                                        value={values.proposed_password}
                                        placeholder={text.new_password}
                                        infoText={text.new_password_info}
                                        name={names.proposed_password}
                                        onChange={e => {
                                            validateInputs(e, names.proposed_password)
                                            handleChange(e)
                                        }}
                                        onFocus={() => {
                                            setFieldTouched(names.proposed_password)
                                        }}
                                        onBlur={() => setTouched({})}
                                        error={errors.proposed_password}
                                        activeInfo={active === names.proposed_password}
                                        handleInfo={(state) => handleInfo(names.proposed_password, state)}
                                        isTouched={touched.proposed_password}
                                        isCheckedPass
                                        icon={checkIsValid(values, names.proposed_password)}
                                        hasRealTimeValidation={true}
                                    />
                                    {Forms.getValidationToolTip(checkIsValid, values, names.proposed_password, formErrors, touched, text.password_requirments, isDarkMode)}
                                </div>
                                <div className="input-container">
                                    <Input
                                        type="password"
                                        value={values.confirm_new_password}
                                        placeholder={text.confirm_new_password}
                                        infoText={text.confirm_new_password_info}
                                        name={names.confirm_new_password}
                                        onChange={e => {
                                            validateInputs(e, names.confirm_new_password, values.proposed_password)
                                            handleChange(e)
                                        }}
                                        onFocus={() => {
                                            setFieldTouched(names.confirm_new_password)
                                        }}
                                        onBlur={() => setTouched({})}
                                        error={errors.confirm_new_password}
                                        activeInfo={active === names.confirm_new_password}
                                        handleInfo={(state) => handleInfo(names.confirm_new_password, state)}
                                        isTouched={touched.confirm_new_password}
                                        isCheckedPass
                                        icon={checkIsValid(values, names.confirm_new_password)}
                                        hasRealTimeValidation={true}
                                    />
                                    {Forms.getValidationToolTip(checkIsValid, values, names.confirm_new_password, formErrors, touched, text.password_requirments, isDarkMode)}
                                </div>
                                <div className='security-delete-actions'>
                                    <SimpleBlueButton
                                        type='button'
                                        outline
                                        disabled={isSubmitting}
                                        onClick={() => handleCancel(resetForm)}
                                    >
                                        {text.cancel}
                                    </SimpleBlueButton>
                                    <SimpleBlueButton
                                        type='submit'
                                        disabled={getButtonStatus(values, isSubmitting)}
                                    >
                                        {isSubmitting && <ButtonLoader/>}
                                        {text.update_password}
                                    </SimpleBlueButton>
                                </div>
                            </form>
                        )}
                    </Formik>
                </div>
            )}
        </div>
    )
}

export default PasswordSection
